import type { UseQuery, UseQuerySubscription } from '@reduxjs/toolkit/dist/query/react/buildHooks'
import { skipToken, type QueryDefinition } from '@reduxjs/toolkit/query'

export type UseDynamicCountProps<T> = {
  totalCount: number | undefined | null
  originalArgs?: T
}

// this key is used in the axiosBaseQuery
// to understand whether the request is a count request or not
export const uniqueCountSymbol = 'E360_only_count_request_key'

export const useGetCountRequestArgs = <T>({
  totalCount,
  originalArgs,
}: UseDynamicCountProps<T>) => {
  if (originalArgs && totalCount === -1) {
    return {
      countRequestArgs: { ...originalArgs, [uniqueCountSymbol]: 1 } as T & {
        [uniqueCountSymbol]: 1
      },
    }
  }
  return {
    countRequestArgs: undefined,
  }
}

export const useTableWithCountQuery = <Q extends QueryDefinition<any, any, any, any>>(
  useQuery: UseQuery<Q>,
  ...args: Parameters<UseQuerySubscription<Q>>
) => {
  const queryResponse = useQuery(...args)

  const totalCount =
    queryResponse.data?.pagination?.totalCount ??
    queryResponse.data?.pagination?.total_count ??
    null

  const { countRequestArgs } = useGetCountRequestArgs({
    totalCount,
    originalArgs: queryResponse.originalArgs,
  })

  const { data: countData } = useQuery(countRequestArgs ?? skipToken)

  const onlyCount = countData?.pagination?.totalCount ?? countData?.pagination?.total_count ?? null

  const count: number | null = totalCount === -1 ? onlyCount : totalCount

  return { ...queryResponse, count }
}
